import { parse_cnpj_from_search } from './client-paths'
import { is_driver_flow_path } from './driver-paths'

export interface AppStoreConfig {
  google_play_app_url?: string
  apple_app_store_url?: string
}

export interface AppStoreLink {
  name: 'Google Play' | 'App Store'
  url: string
  icon_class: 'fab fa-google-play' | 'fab fa-apple'
}

export interface AppStoreLinks {
  google_play: AppStoreLink | null
  app_store: AppStoreLink | null
}

export interface InstallOfferAvailability {
  standalone: boolean
  has_install_session: boolean
  native_app_preferred: boolean
  native_store_available: boolean
  pwa_prompt_available: boolean
  manual_install_available: boolean
}

const GOOGLE_PLAY_APP_URL = 'https://play.google.com/store/apps/details?id=com.nabomba.app'
const APPLE_APP_STORE_URL = 'https://apps.apple.com/br/app/na-bomba-fidelidade/id6792358750'

function official_store_url(raw: unknown, hostname: string, fallback: string): string | null {
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) return fallback
  try {
    const url = new URL(value)
    if (url.protocol !== 'https:' || url.hostname !== hostname) return null
    return url.toString()
  } catch {
    return null
  }
}

export function is_samsung_internet_webapk_blocked(user_agent: string): boolean {
  return /Android/i.test(user_agent) && /SamsungBrowser\//i.test(user_agent)
}

export function prefers_native_app_store(user_agent: string): boolean {
  return /iPhone|iPad|iPod/i.test(user_agent) || is_samsung_internet_webapk_blocked(user_agent)
}

export function should_defer_to_ios_smart_app_banner(
  user_agent: string,
  pathname: string,
  search: string,
  rede_context: boolean,
): boolean {
  return /iPhone|iPad|iPod/i.test(user_agent)
    && is_direct_native_app_access(pathname, search, rede_context)
}

export function is_direct_native_app_access(pathname: string, search: string, rede_context: boolean): boolean {
  if (rede_context || parse_cnpj_from_search(search)) return false
  const clean_path = (String(pathname || '/').split(/[?#]/, 1)[0] ?? '') || '/'
  return clean_path === '/' || is_driver_flow_path(clean_path)
}

export function can_offer_installation(availability: InstallOfferAvailability): boolean {
  if (availability.standalone) return false
  if (availability.native_app_preferred) return availability.native_store_available
  if (!availability.has_install_session) return false
  return availability.pwa_prompt_available || availability.manual_install_available
}

export function normalize_app_store_links(config: AppStoreConfig): AppStoreLinks {
  const google_play_url = official_store_url(config.google_play_app_url, 'play.google.com', GOOGLE_PLAY_APP_URL)
  const app_store_url = official_store_url(config.apple_app_store_url, 'apps.apple.com', APPLE_APP_STORE_URL)
  return {
    google_play: google_play_url
      ? { name: 'Google Play', url: google_play_url, icon_class: 'fab fa-google-play' }
      : null,
    app_store: app_store_url
      ? { name: 'App Store', url: app_store_url, icon_class: 'fab fa-apple' }
      : null,
  }
}

export function preferred_app_store(user_agent: string, links: AppStoreLinks): AppStoreLink | null {
  if (/Android/i.test(user_agent)) return links.google_play
  if (/iPhone|iPad|iPod/i.test(user_agent)) return links.app_store
  return null
}

export function visible_app_stores(user_agent: string, links: AppStoreLinks): AppStoreLink[] {
  const preferred = preferred_app_store(user_agent, links)
  if (preferred) return [preferred]
  return [links.google_play, links.app_store]
    .filter((link): link is AppStoreLink => link !== null)
}
